import _ from 'underscore';
import Promise from 'bluebird';
import { PureKnockoutView } from '@bingads-webui/knockout-view';
import { DeterministicViewForBackBoneView } from '@bingads-webui/deterministic-wrapper-view';
import { Selector } from '@bingads-webui/selector';
import template from './template.pug';
import { LabelsDataSource } from '../util/labels-data-source';
import ViewModel from './selector-viewmodel';
import labelNameTemplate from '../shared/label-name-template.pug';
import checkboxTemplate from './row.checked.pug';

/**
 * create event - When create menu clicked, it fires with current status.
 *
 * @event LabelsSelectorView#create
 * @type {void}
 */

/**
 * cancel event - When cancel button clicked, it fires cancel event.
 *
 * @event LabelsSelectorView#cancel
 * @type {void}
 */

/**
 * cancel event - When apply menu clicked, it fires apply event.
 *
 * @event LabelsSelectorView#apply
 * @type {void}
 */

/**
 * cancel event - When apply menu clicked, it fires apply event.
 *
 * @event LabelsSelectorView#apply
 * @type {void}
 */

/**
 * load:data event - after data loaded, it fires the event.
 *
 * @event LabelsSelectorView#load:data
 * @type {void}
 */

/**
 * change:selection event - after data loaded, it fires the event.
 *
 * @event LabelsSelectorView#change:selection
 * @type {void}
 */

/**
 * LabelsSelectorView: label selector view created based on selector
 * Trigger event LabelsSelectorView#create
 * Trigger event LabelsSelectorView#cancel
 * Trigger event LabelsSelectorView#apply
 * Trigger event LabelsSelectorView# change:selection
 * Trigger event LabelsSelectorView#load:data
 */
export class LabelsSelectorView extends PureKnockoutView {
  /**
   * Initilize labels selector view settings
   *
   * @param {Object} param - An object containing these default options for view:
   * @param {Object} param.dataSource - the datasource for fetching labels, nullable
   * @param {Function} param.dataSource.get - the function that will trigger labels retrival
   * @param {Object} param.checkAll - whether to mark all as selected
   * @param {Object} param.i18n - the localization settings
   * @param {Function} param.i18n.getString - localization settings function
   * @param {String} param.jsdata - jsdata which will be used in labelsDatasource
   * @param {Object} param.dataContextManager - dataContextManager
   * @param {Function} param.dataContextManager.getContext - getContext for request
   * @param {Object} param.cacheService - cacheService
   * @param {Function} param.cacheService.cache - cache function
   * @param {boolean} param.customerLevelLabelEnabled
   *  - true if the customer level label feature is enabled
   * @returns {void} void
  */
  initialize({
    dataSource = null,
    checkAll = false,
    checkMap = {},
    i18n = { getString: _.Identity },
    jsdata,
    dataContextManager,
    cacheService,
    edm,
    scope,
    isForVNext = false,
    customerLevelLabelEnabled = false,
    useSubMenu = false,
  }) {
    this.options = {
      dataSource: dataSource == null ?
        new LabelsDataSource({
          jsdata,
          dataContextManager,
          cacheService,
          edm,
          scope,
          customerLevelLabelEnabled,
        }) :
        dataSource,
      checkAll,
      checkMap,
      i18n,
      customerLevelLabelEnabled,
      useSubMenu,
    };

    this.isForVNext = isForVNext;

    super.initialize(this.options);

    this.initializeSelector();
  }

  get ViewModel() {
    return ViewModel;
  }

  get events() {
    return {
      'click .action': this.onAction,
    };
  }

  get template() {
    return ({ model }) => template(_.extend({ useSubMenu: this.options.useSubMenu }, model, { i18n: this.options.i18n }));
  }

  get className() {
    return 'labels-selector dropdown-menu';
  }

  initializeSelector() {
    this.selector = new Selector({
      checkboxTemplate,
      isForVNext: this.isForVNext,
      busyIndicator: {
        enabled: true,
        showAfterMs: 0,
        showForMs: 200,
      },
      data: {
        checkAll: this.options.checkAll,
        checkMap: this.options.checkMap,
        itemsDisplayLimit: this.options.dataSource.itemsDisplayLimit,
      },
      filter: {
        showSelected: false,
        showChanged: false,
      },
      i18n: this.options.i18n,
      styles: {
        searchIcon: this.isForVNext ?
          ['iconba', 'iconba-Search'] :
          ['glyphicon', 'filter_search_icon_small'],
        ariaLabel: this.options.i18n.getString('Label dropdown selector'),
        searchBoxAriaLabel: this.options.i18n.getString('Search by label name'),
        list: ['table--fixed-layout', 'table--no-border', 'table--no-header'],
        listAttributes: { role: 'presentation' },
        checkboxColumn: ['table__cell--menu-item-style', 'table__cell--icon', 'legacy-label-selector-checkbox', 'clickable'],
        checkboxAttributes: { role: 'checkbox', tabindex: 0 },
        nameColumn: ['table__cell--menu-item-style', 'table__cell--ellipsis-overflow'],
      },
      templates: this.getSelectorTemplates(),
    });

    const thiss = this;

    this.selector.on('request:data', ({ viewFilter, searchFilter, stats }) => {
      thiss.trigger('request:data:beginning');

      const itemsOrPromise = thiss.options.dataSource.get(viewFilter, searchFilter, stats);

      Promise.resolve(itemsOrPromise).finally(() => thiss.trigger('request:data:finished'));

      thiss.selector.setData(itemsOrPromise);
    });

    this.selector.on('load:data', (data) => {
      if (!this.viewModel.loaded()) {
        this.viewModel.hasData(data.hasData);
      }
      this.viewModel.loaded(true);
      this.trigger('load:data', data);
    });

    this.selector.on('change:selection', (stats) => {
      this.viewModel.hasChanges(stats.hasChanges());
      this.trigger('change:selection', stats);
    });

    const selectorViewDeterministic = new DeterministicViewForBackBoneView({ view: this.selector });
    this.setChild('selectorView', selectorViewDeterministic);
  }

  setCheckMap(checkMap) {
    this.selector.setCheckMap(checkMap);
  }

  rerender() {
    this.selector.rerender();
  }

  onAction(event) {
    const action = event.currentTarget.attributes['data-action'].value;
    const stats = this.selector.getStats();

    this.trigger(action, action, stats);
  }

  remove() {
    if (this.selector) {
      this.selector.remove();
      this.selector = null;
    }
    super.remove();
  }

  getSelectorTemplates() {
    return this.options.customerLevelLabelEnabled ? { nameTemplate: labelNameTemplate } : undefined;
  }
}

LabelsSelectorView.ViewFilter = Selector.ViewFilter;
LabelsSelectorView.CheckState = Selector.CheckState;
LabelsSelectorView.CheckTransition = Selector.CheckTransition;
LabelsSelectorView.statCheckMap = Selector.statCheckMap;
